import React, { useState, useMemo } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

/**
 * ProductGrid Component
 * Displays products in a grid for quick selection
 */
export const ProductGrid = ({ products, onSelectProduct, loading, cartItems }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = useMemo(() => {
    const cats = ['all'];
    const uniqueCats = new Set();
    products.forEach(product => {
      if (product.category?.name) {
        uniqueCats.add(product.category.name);
      }
    });
    return [...cats, ...Array.from(uniqueCats).sort()];
  }, [products]);

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesSearch =
        !searchTerm ||
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.sku && product.sku.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesCategory =
        selectedCategory === 'all' ||
        product.category?.name === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [products, searchTerm, selectedCategory]);

  const isInCart = (productId) => {
    return cartItems.some(item => item.productId === productId);
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Search Bar */}
      <div className="p-4 border-b border-gray-200 bg-white">
        <div className="relative mb-4">
          <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full whitespace-nowrap font-medium transition ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:border-gray-400'
              }`}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Products Grid */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading products...</p>
            </div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500">
              <p className="text-lg font-medium">No products found</p>
              <p className="text-sm mt-1">Try adjusting your search or filters</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {filteredProducts.map(product => {
              const inCart = isInCart(product._id);
              const cartItem = cartItems.find(item => item.productId === product._id);

              return (
                <button
                  key={product._id}
                  onClick={() => onSelectProduct(product)}
                  className={`rounded-lg p-3 text-left transition-all duration-200 ${
                    inCart
                      ? 'ring-2 ring-green-500 bg-green-50 border border-green-200'
                      : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  {/* Product Image */}
                  {product.image && (
                    <div className="w-full h-24 bg-gray-100 rounded mb-2 overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Product Name */}
                  <h3 className="font-semibold text-gray-900 text-sm line-clamp-2">
                    {product.name}
                  </h3>

                  {/* Product SKU */}
                  {product.sku && (
                    <p className="text-xs text-gray-500 mt-1">{product.sku}</p>
                  )}

                  {/* Price */}
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-lg font-bold text-gray-900">
                      ₱{product.price?.toFixed(2) || '0.00'}
                    </span>
                    {inCart && (
                      <span className="bg-green-600 text-white px-2 py-1 rounded text-xs font-semibold">
                        {cartItem.quantity > 1 ? `x${cartItem.quantity}` : 'In Cart'}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="p-4 border-t border-gray-200 bg-white text-sm text-gray-600">
        Showing {filteredProducts.length} of {products.length} products
      </div>
    </div>
  );
};

export default ProductGrid;
